// /Users/shawn/💼_WORKSPACE/Projects/FixPriceAI-Desktop/src/App.js
// Purpose: Main desktop application component with drag & drop functionality
// NOT for: Mobile React Native components

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import SmartAIRouter from './services/SmartAIRouter';
import AIVisionService from './services/AIVisionService';
import GroqOnlyAI from './services/GroqOnlyAI';
import { API_KEYS } from './config/apiKeys';
import './App.css';

function App() {
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [userTier, setUserTier] = useState('premium'); // Start with premium for demo
  const [location, setLocation] = useState('Warszawa');
  const [analysisStep, setAnalysisStep] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [apiKeys, setApiKeys] = useState({
    gemini: localStorage.getItem('gemini_api_key') || API_KEYS.GOOGLE_GEMINI,
    groq: localStorage.getItem('groq_api_key') || API_KEYS.GROQ,
    ideogram: localStorage.getItem('ideogram_api_key') || API_KEYS.IDEOGRAM
  });

  // Initialize AI Vision Service
  const [aiVisionService] = useState(new AIVisionService());
  const [groqOnlyAI] = useState(new GroqOnlyAI());

  const analysisSteps = [
    { icon: '🔍', text: 'Quick AI screening', completed: false },
    { icon: '🧠', text: 'Selecting best AI model', completed: false },
    { icon: '💰', text: 'Calculating costs', completed: false },
    { icon: '📊', text: 'Preparing estimate', completed: false },
    { icon: '📋', text: 'Building report', completed: false },
  ];

  const [steps, setSteps] = useState(analysisSteps);
  const [previewImage, setPreviewImage] = useState(null); // For image modal
  const [editingImageId, setEditingImageId] = useState(null); // For name editing

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    // Process multiple files (minimum 5, maximum 20)
    const newImages = acceptedFiles.slice(0, 20).map((file, index) => ({
      id: Date.now() + index,
      file: file,
      name: file.name,
      preview: null,
      analyzed: false,
      result: null
    }));

    // Read all files
    newImages.forEach((imageObj) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        imageObj.preview = e.target.result;
        setSelectedImages(prev => [...prev.filter(img => img.id !== imageObj.id), imageObj]);
      };
      reader.readAsDataURL(imageObj.file);
    });

    setSelectedImages(prev => [...prev, ...newImages]);
    setAnalysisResults([]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.webp']
    },
    multiple: true
  });

  const convertToBase64 = (dataURL) => {
    return dataURL.split(',')[1];
  };

  // Image management functions
  const removeImage = (imageId) => {
    setSelectedImages(prev => prev.filter(img => img.id !== imageId));
    // Also remove from results if exists
    setAnalysisResults(prev => prev.filter(result => result.imageId !== imageId));
  };

  const clearAllImages = () => {
    setSelectedImages([]);
    setAnalysisResults([]);
    setCurrentImageIndex(0);
  };

  const updateImageName = (imageId, newName) => {
    setSelectedImages(prev => prev.map(img =>
      img.id === imageId ? { ...img, name: newName } : img
    ));
    setEditingImageId(null);
  };

  const openImagePreview = (image) => {
    setPreviewImage(image);
  };

  const getImageStatus = (imageId) => {
    const result = analysisResults.find(r => r.imageId === imageId);
    if (!result) return 'pending';
    if (result.result?.error) return 'error';
    return 'analyzed';
  };

  const saveApiKeys = () => {
    Object.keys(apiKeys).forEach(key => {
      if (apiKeys[key]) {
        localStorage.setItem(`${key}_api_key`, apiKeys[key]);
      }
    });
    setShowSettings(false);
  };

  const analyzeImages = async () => {
    if (selectedImages.length === 0) return;

    setIsAnalyzing(true);
    setAnalysisResults([]);
    setSteps(analysisSteps.map(step => ({ ...step, completed: false })));
    setAnalysisStep(0);

    // Start step animation
    const stepInterval = setInterval(() => {
      setAnalysisStep(prev => {
        const next = prev + 1;
        setSteps(prevSteps =>
          prevSteps.map((step, index) => ({
            ...step,
            completed: index < next
          }))
        );

        if (next >= analysisSteps.length) {
          clearInterval(stepInterval);
          return prev;
        }
        return next;
      });
    }, 1500);

    const results = [];

    try {
      // Analyze all images with real AI
      for (let i = 0; i < selectedImages.length; i++) {
        const image = selectedImages[i];
        if (!image.preview) continue;

        console.log(`🧠 AI Analysis: Processing image ${i + 1}/${selectedImages.length}...`);

        try {
          // Use new AI Vision Service for real analysis
          console.log(`🔑 Using API keys: Gemini=${API_KEYS.GOOGLE_GEMINI ? 'SET' : 'MISSING'}, Groq=${API_KEYS.GROQ ? 'SET' : 'MISSING'}`);
          console.log(`📋 Analysis options:`, { location, projectType: 'remont', analysisDepth: userTier === 'premium' ? 'detailed' : 'basic' });
          console.log(`📸 Image file:`, { name: image.file.name, size: image.file.size, type: image.file.type });

          // Quick test to see if CORS is the issue
          try {
            const testResponse = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${API_KEYS.GOOGLE_GEMINI}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                contents: [{ parts: [{ text: "Test - odpowiedz 'OK'" }] }]
              })
            });
            console.log(`🌐 CORS Test Response Status:`, testResponse.status);
          } catch (corsError) {
            console.error(`❌ CORS Error detected:`, corsError);
            alert(`⚠️ CORS PROBLEM!\nGoogle Gemini nie pozwala na wywołania z przeglądarki.\nBłąd: ${corsError.message}`);
          }

          // Use Groq-only AI (no CORS issues)
          const aiResult = await groqOnlyAI.analyzeConstructionImage(
            image.file,
            {
              location: location,
              projectType: 'remont',
              analysisDepth: userTier === 'premium' ? 'detailed' : 'basic'
            }
          );

          console.log(`🎯 Raw AI result:`, aiResult);

          // Temporary debug alert for user
          if (i === 0) { // Only for first image
            alert(`✅ PRAWDZIWA ANALIZA AI DZIAŁA!\nModel: ${aiResult.metadata?.provider || 'AI'}\nKoszt: ${aiResult.podsumowanie?.koszt_calkowity || 'brak'} PLN`);
          }

          // Convert AI result to expected format
          const convertedResult = convertAIResultToExpectedFormat(aiResult, image.name);

          results.push({
            imageId: image.id,
            imageName: image.name,
            result: convertedResult
          });

          console.log(`✅ AI analysis completed for ${image.name}`);

        } catch (imageError) {
          console.error(`❌ Failed to analyze ${image.name}:`, imageError);

          // Create a proper error result with real AI attempt
          results.push({
            imageId: image.id,
            imageName: image.name,
            result: {
              problem_type: "Błąd analizy AI",
              description: `Nie udało się przeanalizować obrazu: ${imageError.message}. Spróbuj ponownie z innym zdjęciem.`,
              severity: "unknown",
              estimated_cost: { min: 0, max: 0, confidence: "error" },
              timeline: { days: 0, description: "Analiza nieudana" },
              materials: [],
              labor_cost: 0,
              steps: ["Spróbuj ponownie z lepszym zdjęciem"],
              recommendations: ["Spróbuj ponownie z jaśniejszym lub lepszym zdjęciem"],
              confidence: 0,
              analysis_notes: "Błąd analizy AI"
            }
          });
        }

        // Update progress
        setCurrentImageIndex(i);
      }

      // Complete all steps
      setSteps(prevSteps =>
        prevSteps.map(step => ({ ...step, completed: true }))
      );

      setTimeout(() => {
        setAnalysisResults(results);
        setIsAnalyzing(false);
        clearInterval(stepInterval);
      }, 1000);

    } catch (error) {
      console.error('Analysis failed:', error);
      setIsAnalyzing(false);
      clearInterval(stepInterval);

      setAnalysisResults([{
        imageId: 'error',
        imageName: 'Error',
        result: {
          error: true,
          problem_type: "Błąd analizy AI",
          description: error.message || "Nie udało się przeanalizować zdjęć z użyciem AI. Sprawdź połączenie internetowe.",
          tips: [
            "🧠 Google Gemini AI: DOSTĘPNY i skonfigurowany",
            "⚡ Groq AI (darmowy): DOSTĘPNY jako backup",
            "🖼️ Ideogram AI: DOSTĘPNY dla obrazów",
            "🔧 Sprawdź jakość i oświetlenie zdjęć",
            "🌐 Upewnij się że masz stabilne połączenie internetowe"
          ]
        }
      }]);
    }
  };

  const exportReport = () => {
    if (analysisResults.length === 0) return;

    const reportData = {
      timestamp: new Date().toISOString(),
      totalImages: selectedImages.length,
      location: location,
      userTier: userTier,
      images: selectedImages.map(img => img.name),
      analyses: analysisResults
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `FixPriceAI-MultiReport-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount);
  };

  // Convert AI Vision Service result to expected App format
  const convertAIResultToExpectedFormat = (aiResult, imageName) => {
    if (!aiResult || aiResult.error) {
      return {
        error: true,
        problem_type: "Błąd analizy AI",
        description: "Nie udało się przeanalizować tego zdjęcia",
        tips: ["Sprawdź jakość zdjęcia", "Upewnij się że pokazuje problem budowlany"]
      };
    }

    // Convert structured AI result to App's expected format
    const totalCost = aiResult.podsumowanie?.koszt_calkowity || 0;
    const timelineDays = aiResult.podsumowanie?.czas_calkowity_dni || 1;

    return {
      problem_type: aiResult.identyfikacja?.typ_pomieszczenia || "Analiza pomieszczenia",
      description: `Powierzchnia: ${aiResult.identyfikacja?.powierzchnia_m2 || 'brak danych'} m². ${aiResult.identyfikacja?.stan_ogolny || 'Wymaga remontu'}`,
      estimated_cost: {
        min: Math.round(totalCost * 0.8), // -20% for range
        max: Math.round(totalCost * 1.2), // +20% for range
        breakdown: aiResult.prace_do_wykonania?.map(praca => ({
          category: praca.nazwa,
          cost_min: praca.koszt_materialow + praca.koszt_robocizny,
          cost_max: Math.round((praca.koszt_materialow + praca.koszt_robocizny) * 1.15)
        })) || []
      },
      timeline: {
        days: timelineDays,
        phases: aiResult.prace_do_wykonania?.map(praca => ({
          phase: praca.nazwa,
          duration_days: praca.czas_dni || 1,
          description: praca.opis || praca.nazwa
        })) || []
      },
      tips: [
        ...((aiResult.podsumowanie?.zalecenia || []).slice(0, 3)),
        `🏪 ${aiResult.market_insights?.supplier_recommendations?.[0] || 'Sprawdź lokalne sklepy budowlane'}`,
        `📍 Ceny dostosowane do regionu ${aiResult.market_insights?.location || 'Warszawa'}`,
        `⏰ ${aiResult.market_insights?.seasonal_note || 'Planuj remont z wyprzedzeniem'}`
      ].filter(tip => tip && tip.length > 0),
      ai_provider: aiResult.metadata?.provider || 'gemini',
      confidence: aiResult.metadata?.confidence || 0.85,
      enhanced_data: {
        detailed_breakdown: aiResult.prace_do_wykonania || [],
        market_insights: aiResult.market_insights || {},
        regional_adjustments: {
          location: aiResult.market_insights?.location,
          multiplier: aiResult.podsumowanie?.regional_multiplier || 1.0
        }
      }
    };
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <h1>🏗️ FixPriceAI Desktop</h1>
            <p>Inteligentna wycena napraw budowlanych</p>
          </div>

          <div className="header-controls">
            <div className="tier-selector">
              <label>Plan:</label>
              <select
                value={userTier}
                onChange={(e) => setUserTier(e.target.value)}
                disabled={isAnalyzing}
              >
                <option value="free">Darmowy</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div className="location-selector">
              <label>Lokalizacja:</label>
              <select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={isAnalyzing}
              >
                <optgroup label="Miasta wojewódzkie">
                  <option value="Warszawa">Warszawa (mazowieckie)</option>
                  <option value="Kraków">Kraków (małopolskie)</option>
                  <option value="Gdańsk">Gdańsk (pomorskie)</option>
                  <option value="Wrocław">Wrocław (dolnośląskie)</option>
                  <option value="Poznań">Poznań (wielkopolskie)</option>
                  <option value="Katowice">Katowice (śląskie)</option>
                  <option value="Lublin">Lublin (lubelskie)</option>
                  <option value="Szczecin">Szczecin (zachodniopomorskie)</option>
                  <option value="Białystok">Białystok (podlaskie)</option>
                  <option value="Bydgoszcz">Bydgoszcz (kujawsko-pomorskie)</option>
                  <option value="Olsztyn">Olsztyn (warmińsko-mazurskie)</option>
                  <option value="Rzeszów">Rzeszów (podkarpackie)</option>
                  <option value="Kielce">Kielce (świętokrzyskie)</option>
                  <option value="Opole">Opole (opolskie)</option>
                  <option value="Zielona Góra">Zielona Góra (lubuskie)</option>
                  <option value="Łódź">Łódź (łódzkie)</option>
                </optgroup>
                <optgroup label="Duże miasta">
                  <option value="Sosnowiec">Sosnowiec</option>
                  <option value="Toruń">Toruń</option>
                  <option value="Gliwice">Gliwice</option>
                  <option value="Zabrze">Zabrze</option>
                  <option value="Bytom">Bytom</option>
                  <option value="Częstochowa">Częstochowa</option>
                  <option value="Radom">Radom</option>
                  <option value="Ruda Śląska">Ruda Śląska</option>
                  <option value="Rybnik">Rybnik</option>
                  <option value="Tychy">Tychy</option>
                  <option value="Dąbrowa Górnicza">Dąbrowa Górnicza</option>
                  <option value="Płock">Płock</option>
                  <option value="Elbląg">Elbląg</option>
                  <option value="Wałbrzych">Wałbrzych</option>
                  <option value="Włocławek">Włocławek</option>
                  <option value="Tarnów">Tarnów</option>
                  <option value="Chorzów">Chorzów</option>
                  <option value="Koszalin">Koszalin</option>
                  <option value="Kalisz">Kalisz</option>
                  <option value="Legnica">Legnica</option>
                </optgroup>
                <optgroup label="Średnie miasta">
                  <option value="Grudziądz">Grudziądz</option>
                  <option value="Słupsk">Słupsk</option>
                  <option value="Jaworzno">Jaworzno</option>
                  <option value="Jastrzębie-Zdrój">Jastrzębie-Zdrój</option>
                  <option value="Jelenia Góra">Jelenia Góra</option>
                  <option value="Nowy Sącz">Nowy Sącz</option>
                  <option value="Konin">Konin</option>
                  <option value="Piotrków Trybunalski">Piotrków Trybunalski</option>
                  <option value="Siedlce">Siedlce</option>
                  <option value="Inowrocław">Inowrocław</option>
                  <option value="Piła">Piła</option>
                  <option value="Ostrowiec Świętokrzyski">Ostrowiec Świętokrzyski</option>
                  <option value="Stargard">Stargard</option>
                  <option value="Gniezno">Gniezno</option>
                  <option value="Siemianowice Śląskie">Siemianowice Śląskie</option>
                  <option value="Będzin">Będzin</option>
                  <option value="Zgierz">Zgierz</option>
                  <option value="Zawiercie">Zawiercie</option>
                  <option value="Pruszków">Pruszków</option>
                  <option value="Legionowo">Legionowo</option>
                </optgroup>
              </select>
            </div>

            <button
              className="settings-btn"
              onClick={() => setShowSettings(true)}
              disabled={isAnalyzing}
            >
              ⚙️ Ustawienia API
            </button>
          </div>
        </div>
      </header>

      <main className="app-main">
        <div className="main-grid">
          {/* Left Panel - Image Upload */}
          <div className="left-panel">
            <div className="upload-section">
              <h2>📸 Wgraj zdjęcia problemów budowlanych</h2>

              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'active' : ''} ${selectedImages.length > 0 ? 'has-images' : ''}`}
              >
                <input {...getInputProps()} />

                {selectedImages.length > 0 ? (
                  <div className="images-preview">
                    <div className="images-header">
                      <h3>📷 Zdjęcia ({selectedImages.length})</h3>
                      <button
                        className="clear-all-btn"
                        onClick={clearAllImages}
                        disabled={isAnalyzing}
                      >
                        🗑️ Usuń wszystkie
                      </button>
                    </div>

                    <div className="images-grid">
                      {selectedImages.map((image, index) => (
                        <div key={image.id} className={`image-thumbnail ${getImageStatus(image.id)}`}>
                          {/* Remove button */}
                          <button
                            className="remove-image-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(image.id);
                            }}
                            disabled={isAnalyzing}
                          >
                            ✕
                          </button>

                          {/* Status indicator */}
                          <div className={`status-indicator ${getImageStatus(image.id)}`}>
                            {getImageStatus(image.id) === 'analyzed' && '✅'}
                            {getImageStatus(image.id) === 'error' && '❌'}
                            {getImageStatus(image.id) === 'pending' && '⏳'}
                          </div>

                          {/* Image preview */}
                          {image.preview && (
                            <img
                              src={image.preview}
                              alt={`Preview ${index + 1}`}
                              onClick={() => openImagePreview(image)}
                              style={{cursor: 'pointer'}}
                            />
                          )}

                          {/* Editable name */}
                          <div className="image-info">
                            {editingImageId === image.id ? (
                              <input
                                type="text"
                                defaultValue={image.name}
                                autoFocus
                                onBlur={(e) => updateImageName(image.id, e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') updateImageName(image.id, e.target.value);
                                  if (e.key === 'Escape') setEditingImageId(null);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingImageId(image.id);
                                }}
                                style={{cursor: 'pointer'}}
                                title="Kliknij żeby edytować nazwę"
                              >
                                {image.name.length > 20 ? `${image.name.substring(0, 17)}...` : image.name}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="images-summary">
                      <p>📁 {selectedImages.length} zdjęć wgranych</p>
                      <button
                        className="change-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImages([]);
                          setAnalysisResults([]);
                        }}
                      >
                        Zmień zdjęcia
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="dropzone-content">
                    <div className="dropzone-icon">📷</div>
                    <h3>Przeciągnij zdjęcia tutaj (min. 5)</h3>
                    <p>lub kliknij aby wybrać pliki</p>
                    <small>
                      Obsługiwane formaty: JPG, PNG, GIF, BMP, WebP<br/>
                      Maksymalnie 20 zdjęć jednocześnie
                    </small>
                  </div>
                )}
              </div>

              {selectedImages.length >= 1 && (
                <button
                  className="analyze-btn"
                  onClick={analyzeImages}
                  disabled={isAnalyzing}
                >
                  {isAnalyzing
                    ? `🔄 Analizowanie ${currentImageIndex + 1}/${selectedImages.length}...`
                    : `🧠 Analizuj ${selectedImages.length} ${selectedImages.length === 1 ? 'zdjęcie' : 'zdjęć'}`
                  }
                </button>
              )}
            </div>

            {/* Analysis Progress */}
            {isAnalyzing && (
              <div className="analysis-progress">
                <h3>🤖 Multi-model AI w akcji</h3>
                <p className="progress-subtitle">
                  Używamy najlepszego modelu AI dla Twojego przypadku
                </p>

                <div className="steps-container">
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step ${step.completed ? 'completed' : ''} ${index === analysisStep ? 'current' : ''}`}
                    >
                      <span className="step-icon">
                        {step.completed ? '✅' : index === analysisStep ? '⏳' : step.icon}
                      </span>
                      <span className="step-text">{step.text}</span>
                    </div>
                  ))}
                </div>

                <div className="analysis-time">
                  ⏱️ Multi-model analiza: 12-18 sekund
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="right-panel">
            {analysisResults.length > 0 ? (
              <div className="results-section">
                <div className="results-header">
                  <h2>📋 Analiza {analysisResults.length} zdjęć</h2>
                  <div className="results-summary">
                    <div className="ai-badge">
                      🧠 Multi-model AI • {userTier === 'premium' ? 'Premium' : 'Darmowy'}
                    </div>
                    <button className="export-btn" onClick={exportReport}>
                      💾 Eksportuj wszystkie raporty
                    </button>
                  </div>
                </div>

                <div className="multi-results">
                  {analysisResults.map((result, index) => {
                    const analysisResult = result.result;
                    return (
                      <div key={result.imageId} className="result-item">
                        <div className="result-header">
                          <h3>📷 {result.imageName}</h3>
                          <span className="result-index">#{index + 1}</span>
                        </div>

                        <div className="results-content">
                          {analysisResult.error ? (
                            /* Error State */
                            <div className="result-card error-card">
                              <h3>❌ Błąd analizy</h3>
                              <p>Wystąpił problem podczas analizy tego zdjęcia.</p>
                            </div>
                          ) : (
                            /* Success State - Simplified summary for multi-image */
                            <div className="result-summary-card">
                              <h4>🔍 {analysisResult.problem_type}</h4>
                              <div className="cost-summary">
                                💰 {formatCurrency(analysisResult.estimated_cost.min)} - {formatCurrency(analysisResult.estimated_cost.max)}
                              </div>
                              <div className="timeline-summary">
                                ⏱️ {analysisResult.timeline.days} dni
                              </div>

                              <div className="result-card tips-card">
                                <h3>💡 Rozwiązania</h3>
                                <ul className="tips-list">
                                  {analysisResult.tips?.map((tip, tipIndex) => (
                                    <li key={tipIndex}>{tip}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-content">
                  <div className="no-results-icon">📊</div>
                  <h3>Wgraj zdjęcie aby rozpocząć</h3>
                  <p>
                    FixPriceAI Desktop analizuje zdjęcia problemów budowlanych
                    i dostarcza szczegółowe wyceny z polskimi cenami.
                  </p>
                  <div className="features-list">
                    <div className="feature">🧠 Multi-model AI analysis</div>
                    <div className="feature">💰 Regionalne ceny Polski</div>
                    <div className="feature">📋 Szczegółowe raporty</div>
                    <div className="feature">⚡ Inteligentna optymalizacja kosztów</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-left">
            <span>FixPriceAI Desktop v2.0.0</span>
            <span>•</span>
            <span>Powered by SmartAIRouter</span>
          </div>
          <div className="footer-right">
            <span>🇵🇱 Made for Polish construction market</span>
          </div>
        </div>
      </footer>

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚙️ Konfiguracja API</h3>
              <button
                className="close-btn"
                onClick={() => setShowSettings(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p>Skonfiguruj klucze API dla pełnej funkcjonalności aplikacji:</p>

              <div className="api-key-section">
                <label>🧠 Google Gemini API Key:</label>
                <input
                  type="password"
                  placeholder="Wprowadź klucz API Gemini..."
                  value={apiKeys.gemini}
                  onChange={(e) => setApiKeys(prev => ({...prev, gemini: e.target.value}))}
                />
                <small>Gemini Vision API - najlepszy do analizy obrazów budowlanych</small>
              </div>

              <div className="api-key-section">
                <label>🤖 OpenAI API Key:</label>
                <input
                  type="password"
                  placeholder="Wprowadź klucz API OpenAI..."
                  value={apiKeys.openai}
                  onChange={(e) => setApiKeys(prev => ({...prev, openai: e.target.value}))}
                />
                <small>GPT-4 Vision - backup dla Gemini</small>
              </div>

              <div className="api-key-section">
                <label>🎯 Claude API Key:</label>
                <input
                  type="password"
                  placeholder="Wprowadź klucz API Claude..."
                  value={apiKeys.claude}
                  onChange={(e) => setApiKeys(prev => ({...prev, claude: e.target.value}))}
                />
                <small>Anthropic Claude - zaawansowana analiza tekstu</small>
              </div>

              <div className="api-info">
                <h4>💡 Informacje o API:</h4>
                <ul>
                  <li><strong>Gemini:</strong> Bezpłatny tier dostępny, najlepszy do obrazów</li>
                  <li><strong>OpenAI:</strong> Płatny, wysokiej jakości analiza</li>
                  <li><strong>Claude:</strong> Płatny, świetny do szczegółowej analizy</li>
                  <li><strong>Bez API:</strong> Aplikacja działa w trybie demo</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowSettings(false)}
              >
                Anuluj
              </button>
              <button
                className="save-btn"
                onClick={saveApiKeys}
              >
                💾 Zapisz ustawienia
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="modal-overlay" onClick={() => setPreviewImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📷 {previewImage.name}</h3>
              <button
                className="close-btn"
                onClick={() => setPreviewImage(null)}
              >
                ✕
              </button>
            </div>

            <div className="image-modal-body">
              <img
                src={previewImage.preview}
                alt={previewImage.name}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
              />

              <div className="image-details">
                <p><strong>Nazwa:</strong> {previewImage.name}</p>
                <p><strong>Rozmiar:</strong> {Math.round(previewImage.file.size / 1024)} KB</p>
                <p><strong>Status:</strong>
                  {getImageStatus(previewImage.id) === 'analyzed' && ' ✅ Przeanalizowane'}
                  {getImageStatus(previewImage.id) === 'error' && ' ❌ Błąd analizy'}
                  {getImageStatus(previewImage.id) === 'pending' && ' ⏳ Czeka na analizę'}
                </p>
              </div>

              <div className="image-actions">
                <button
                  className="remove-btn"
                  onClick={() => {
                    removeImage(previewImage.id);
                    setPreviewImage(null);
                  }}
                  disabled={isAnalyzing}
                >
                  🗑️ Usuń zdjęcie
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;